import type { TFunction } from 'i18next'

/**
 * Mise en mots des durées de l'espace apprenant (#74).
 *
 * Ici on ne fait que FORMATER : l'analyse du format SCORM — `PT1H30M` en 2004,
 * `0000:30:00.0` en 1.2 — reste côté API, qui sert déjà des secondes
 * (`totalTimeSeconds`). Deux analyseurs pour un même format finissent par ne
 * plus annoncer la même durée du même module, une fois sur l'attestation et une
 * fois à l'écran.
 */

export type SplitDuration = { hours: number, minutes: number }

/**
 * Heures et minutes d'une durée en secondes, ou null si elle n'a rien à dire.
 *
 * Null pour une durée absente, négative ou nulle : l'écran n'affiche alors
 * rien. « 0 min » se lirait comme un module vide, alors que la vérité est
 * qu'on ne sait pas — un paquet SCORM qui ne remonte pas son temps est banal.
 */
export function splitDuration(totalSeconds?: number | null): SplitDuration | null {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds <= 0) return null

  let hours = Math.floor(totalSeconds / 3600)
  let minutes = Math.round((totalSeconds % 3600) / 60)
  // L'arrondi des minutes peut atteindre 60 : sans report, on afficherait
  // « 1 h 60 ».
  if (minutes === 60) {
    hours += 1
    minutes = 0
  }
  return { hours, minutes }
}

/** Durée en secondes, rendue lisible (« 1 h 30 », « 45 min », « < 1 min »). */
export function formatSeconds(totalSeconds: number | null | undefined, t: TFunction): string | null {
  const split = splitDuration(totalSeconds)
  if (!split) return null
  const { hours, minutes } = split

  if (hours > 0) {
    return minutes === 0
      ? t('duration.hours', { count: hours })
      : t('duration.hoursMinutes', { hours, minutes: String(minutes).padStart(2, '0') })
  }
  if (minutes > 0) return t('duration.minutes', { count: minutes })
  // Moins d'une minute mais plus de zéro : quelqu'un a bien ouvert le module.
  return t('duration.lessThanMinute')
}

/** Idem depuis des minutes — la forme sous laquelle les modules déclarent leur durée. */
export function formatMinutes(minutes: number | null | undefined, t: TFunction): string | null {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return null
  return formatSeconds(minutes * 60, t)
}

/**
 * Durée estimée de ce qu'il RESTE à faire dans un parcours.
 *
 * Null dès qu'une étape restante ne déclare pas sa durée : annoncer « 20 min »
 * alors que deux modules sur cinq n'ont pas de durée connue serait un chiffre
 * faux, et c'est sur ce chiffre-là que l'apprenant décide s'il commence
 * maintenant. Mieux vaut ne rien dire que sous-estimer.
 */
export function remainingMinutes(
  steps: { module?: { durationMinutes?: number | null } | null, myTracking?: { completionStatus?: string | null } | null }[],
): number | null {
  const todo = steps.filter(s => s.myTracking?.completionStatus !== 'COMPLETED')
  if (todo.length === 0) return null

  let total = 0
  for (const step of todo) {
    const minutes = step.module?.durationMinutes
    if (minutes == null || minutes <= 0) return null
    total += minutes
  }
  return total
}
